import { getRelativeLink } from "@talladega/helpers";
import { Button, ButtonSpacing } from "@talladega/components";
import Link from "next/link";
import { Dispatch, ReactNode, SetStateAction } from "react";
import { useRouter } from "next/router";
import useUtmParamsFromQuery from "@modules/query-string/hooks/useUtmParamsFromQuery";
import { ActionButton, CmsButtonType } from "@talladega/types";
import GetButtonStyle from "../helpers/getButtonStyle";
import { ButtonModalState } from "./CmsButtonModalContainer";

type Props = {
  button: CmsButtonType;
  setButtonModalState: Dispatch<SetStateAction<ButtonModalState>>;
  className?: string;
  onClick?: () => void;
  rightIcon?: ReactNode;
  htmlIdRef?: React.MutableRefObject<HTMLDivElement | null>;
  spacing?: ButtonSpacing;
};

const CmsButton = ({
  button,
  onClick,
  setButtonModalState,
  rightIcon,
  className,
  htmlIdRef,
  spacing,
}: Props) => {
  const router = useRouter();
  const locale = router.locale || "en";
  const utmParams = useUtmParamsFromQuery();

  const getButton = () => {
    const btn = (
      <Button
        className={className}
        fill={button.buttonType || "filled"}
        buttonStyle={GetButtonStyle(button) || "primary"}
        spacing={spacing}
        onClick={() => {
          if (!button.isButtonLink) {
            if (button.actionButton === ActionButton.GravityForm) {
              setButtonModalState({
                show: true,
                actionType: button.actionButton as unknown as ActionButton,
                formProps:
                  button.actionButton === ActionButton.GravityForm
                    ? button.formData?.formSide
                    : undefined,
              });
            } else if (
              button.actionButton === ActionButton.IcoModal &&
              !onClick
            ) {
              window.acIco.destroy();
              window.acIco.initModal("ico", "throttle", {
                locale,
                utmParams: {
                  utmSource: utmParams.utmSource ?? button?.icoModal?.utmSource,
                  utmMedium: utmParams.utmMedium ?? button?.icoModal?.utmMedium,
                  utmCampaign:
                    utmParams.utmCampaign ?? button?.icoModal?.utmCampaign,
                  utmContent:
                    utmParams.utmContent ?? button?.icoModal?.utmContent,
                },
              });
            }
          }

          if (onClick) {
            onClick();
          }
          if (window.location.hash) {
            htmlIdRef?.current?.scrollIntoView({
              behavior: "smooth",
            });
          }
        }}
        rightIcon={rightIcon}
        aria-label={`${
          button.isButtonLink
            ? button?.buttonLink?.title ?? ""
            : button?.actionButtonTitle
        }`}
      >
        {button.isButtonLink
          ? button?.buttonLink?.title ?? ""
          : button?.actionButtonTitle}
      </Button>
    );

    if (button?.isButtonLink && button?.buttonLink?.url) {
      let linkUrl = button?.buttonLink?.url;

      if (button?.persistQueryParams) {
        const queryParams = router.asPath.split("?")[1];
        linkUrl = queryParams
          ? `${button.buttonLink?.url}?${queryParams}`
          : button.buttonLink?.url;
      }

      return (
        <Link
          href={getRelativeLink(linkUrl, "throttle.ca")}
          target={button.buttonLink?.target || ""}
        >
          {btn}
        </Link>
      );
    }
    return btn;
  };

  return getButton();
};

export default CmsButton;
